import React from "react";
import { Link } from "react-router-dom";
import logo from "../imgs/logo.png";

const DataDeletion = () => {
  return (
    <div className="data-deletion-wrapper">
      <div className="data-deletion-container">
        {/* Header */}
        <div className="header-section">
          <div className="logo-container">
            <img src={logo} alt="Viralz Logo" className="logo" />
          </div>
          <h1 className="page-title">Data Deletion Request - Viralz App</h1>
          <p className="page-subtitle">Request Account and Data Deletion</p>
        </div>

        {/* Main Content */}
        <div className="content-section">
          <div className="deletion-methods">
            <h2>How to Request Data Deletion</h2>

            {/* Option 1 */}
            <div className="method-card recommended">
              <div className="method-header">
                <h3>Option 1: Delete Account In-App (Recommended)</h3>
                <span className="recommended-badge">Recommended</span>
              </div>
              <ol className="steps-list">
                <li>Open the Viralz app on your device</li>
                <li>
                  Go to <strong>Profile</strong> (tap your profile icon in the
                  top-right corner)
                </li>
                <li>Scroll down to the bottom of the page</li>
                <li>
                  Tap <strong>"Delete Account"</strong>
                </li>
                <li>Follow the prompts to confirm deletion</li>
              </ol>
            </div>

            {/* Option 2 */}
            <div className="method-card">
              <h3>Option 2: Email Request</h3>
              <p>
                Send an email to:{" "}
                <a href="mailto:support@viralzapp.com" className="email-link">
                  support@viralzapp.com
                </a>
              </p>
              <ul className="email-requirements">
                <li>
                  <strong>Subject:</strong> Account Deletion Request
                </li>
                <li>
                  <strong>Include:</strong> Your registered email address and
                  TikTok username (if connected)
                </li>
              </ul>
            </div>
          </div>

          {/* What Data Will Be Deleted */}
          <div className="data-section">
            <h2>What Data Will Be Deleted</h2>
            <p>
              When you request account deletion, the following data will be{" "}
              <strong>permanently removed</strong>:
            </p>

            <div className="data-categories">
              <div className="data-category">
                <h4>✅ Account Information</h4>
                <ul>
                  <li>Your email address and profile information</li>
                  <li>Login credentials and authentication tokens</li>
                </ul>
              </div>

              <div className="data-category">
                <h4>✅ TikTok Data</h4>
                <ul>
                  <li>Connected TikTok username</li>
                  <li>TikTok analytics and performance data</li>
                  <li>AI-generated analysis and insights</li>
                </ul>
              </div>

              <div className="data-category">
                <h4>✅ App Data</h4>
                <ul>
                  <li>All AI-generated reports and recommendations</li>
                  <li>User preferences and settings</li>
                  <li>Habit tracking data and streaks</li>
                  <li>Notification preferences</li>
                </ul>
              </div>

              <div className="data-category">
                <h4>✅ Analytics Data</h4>
                <ul>
                  <li>App usage statistics</li>
                  <li>Feature interaction data</li>
                  <li>Performance metrics</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Data Retention Policy */}
          <div className="retention-section">
            <h2>Data Retention Policy</h2>

            <div className="retention-card">
              <h4>📋 What May Be Retained (Temporarily)</h4>
              <ul>
                <li>
                  Anonymous analytics data: Up to <strong>30 days</strong> (for
                  service improvement)
                </li>
                <li>
                  Billing/transaction records: As required by law (typically 7
                  years)
                </li>
                <li>
                  Crash logs and error reports: Up to <strong>90 days</strong>{" "}
                  (anonymized)
                </li>
              </ul>
            </div>
          </div>

          {/* Timeline */}
          <div className="timeline-section">
            <h2>Timeline for Deletion</h2>
            <div className="timeline">
              <div className="timeline-item">
                <div className="timeline-marker immediate"></div>
                <div className="timeline-content">
                  <h4>Immediate</h4>
                  <p>Account access is revoked</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-marker hours24"></div>
                <div className="timeline-content">
                  <h4>Within 24 hours</h4>
                  <p>Personal data deleted from active systems</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-marker days30"></div>
                <div className="timeline-content">
                  <h4>Within 30 days</h4>
                  <p>All data purged from backups and analytics systems</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-marker billing"></div>
                <div className="timeline-content">
                  <h4>Billing records</h4>
                  <p>Retained only as required by applicable laws</p>
                </div>
              </div>
            </div>
          </div>

          {/* Important Notes */}
          <div className="notes-section">
            <div className="warning-card">
              <h3>⚠️ Before Deleting Your Account:</h3>
              <ul>
                <li>
                  Cancel any active subscriptions through the App Store/Google
                  Play
                </li>
                <li>
                  Export any data you wish to keep (the app does not provide
                  data export)
                </li>
                <li>This action cannot be undone</li>
              </ul>
            </div>

            <div className="security-card">
              <h3>🔒 Privacy & Security:</h3>
              <ul>
                <li>All data deletion requests are processed securely</li>
                <li>We do not retain personal information after deletion</li>
                <li>
                  Your data will not be shared with third parties during the
                  deletion process
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Support */}
          <div className="contact-section">
            <h2>Contact Support</h2>
            <p>If you have questions about data deletion or need assistance:</p>

            <div className="contact-info">
              <div className="contact-item">
                <span className="contact-icon">📧</span>
                <div>
                  <strong>Email:</strong>{" "}
                  <a href="mailto:support@viralzapp.com">
                    support@viralzapp.com
                  </a>
                </div>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📱</span>
                <div>
                  <strong>App:</strong> Viralz - TikTok Growth & Analytics
                </div>
              </div>
              <div className="contact-item">
                <span className="contact-icon">🌐</span>
                <div>
                  <strong>Website:</strong>{" "}
                  <a href="https://viralzapp.com">https://viralzapp.com</a>
                </div>
              </div>
            </div>

            <div className="response-time">
              <strong>Response Time:</strong> We typically respond to deletion
              requests within 24-48 hours.
            </div>
          </div>

          {/* Footer Info */}
          <div className="footer-info">
            <p>
              <em>Last updated: January 2025</em>
            </p>
            <p>
              <em>
                This page complies with Google Play Store data deletion
                requirements.
              </em>
            </p>
          </div>

          {/* Back to Home */}
          <div className="back-section">
            <Link to="/" className="back-button">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>

      {/* CSS Styles */}
      <style jsx>{`
        .data-deletion-wrapper {
          min-height: 100vh;
          background: #000000;
          color: #ffffff;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            sans-serif;
          padding: 40px 20px;
        }

        .data-deletion-container {
          max-width: 900px;
          margin: 0 auto;
          background: linear-gradient(145deg, #1a1a1a, #0d0d0d);
          border-radius: 20px;
          padding: 40px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
          border: 1px solid #333333;
        }

        .header-section {
          text-align: center;
          margin-bottom: 50px;
          padding-bottom: 30px;
          border-bottom: 1px solid #333333;
        }

        .logo-container {
          width: 80px;
          height: 80px;
          background: #000000;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 30px;
          box-shadow: 0 0 20px #fe2c55, 0 0 40px rgba(254, 44, 85, 0.3);
          border: 2px solid #fe2c55;
        }

        .logo {
          width: 40px;
          height: 40px;
        }

        .page-title {
          font-size: 2.5rem;
          font-weight: 800;
          margin: 0 0 15px 0;
          background: linear-gradient(135deg, #fe2c55, #ff6b9d);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .page-subtitle {
          font-size: 1.3rem;
          color: #cccccc;
          margin: 0;
          font-weight: 500;
        }

        .content-section {
          line-height: 1.7;
        }

        .content-section h2 {
          font-size: 2rem;
          font-weight: 700;
          margin: 40px 0 25px 0;
          color: #ffffff;
          border-left: 4px solid #fe2c55;
          padding-left: 20px;
        }

        .content-section h3 {
          font-size: 1.4rem;
          font-weight: 600;
          margin: 25px 0 15px 0;
          color: #ffffff;
        }

        .content-section h4 {
          font-size: 1.2rem;
          font-weight: 600;
          margin: 20px 0 10px 0;
          color: #fe2c55;
        }

        .method-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 15px;
          padding: 25px;
          margin: 20px 0;
          backdrop-filter: blur(10px);
        }

        .method-card.recommended {
          border: 2px solid #fe2c55;
          background: rgba(254, 44, 85, 0.1);
        }

        .method-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }

        .recommended-badge {
          background: linear-gradient(135deg, #fe2c55, #ff6b9d);
          color: white;
          padding: 5px 12px;
          border-radius: 12px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .steps-list {
          margin: 15px 0;
          padding-left: 20px;
        }

        .steps-list li {
          margin: 10px 0;
          font-size: 1rem;
        }

        .email-link {
          color: #fe2c55;
          text-decoration: none;
          font-weight: 600;
          transition: color 0.3s ease;
        }

        .email-link:hover {
          color: #ff6b9d;
        }

        .email-requirements {
          list-style: none;
          padding: 15px 0;
          margin: 0;
        }

        .email-requirements li {
          margin: 8px 0;
        }

        .data-categories {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 20px;
          margin: 25px 0;
        }

        .data-category {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 20px;
        }

        .data-category ul {
          margin: 10px 0;
          padding-left: 20px;
        }

        .data-category li {
          margin: 8px 0;
          font-size: 0.95rem;
        }

        .retention-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 20px;
          margin: 20px 0;
        }

        .timeline {
          margin: 25px 0;
        }

        .timeline-item {
          display: flex;
          align-items: flex-start;
          margin: 20px 0;
          padding: 15px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .timeline-item:last-child {
          border-bottom: none;
        }

        .timeline-marker {
          width: 15px;
          height: 15px;
          border-radius: 50%;
          margin-right: 20px;
          margin-top: 5px;
          flex-shrink: 0;
        }

        .timeline-marker.immediate {
          background: #fe2c55;
          box-shadow: 0 0 10px #fe2c55;
        }

        .timeline-marker.hours24 {
          background: #ff6b9d;
          box-shadow: 0 0 10px #ff6b9d;
        }

        .timeline-marker.days30 {
          background: #ffaa66;
          box-shadow: 0 0 10px #ffaa66;
        }

        .timeline-marker.billing {
          background: #66aaff;
          box-shadow: 0 0 10px #66aaff;
        }

        .timeline-content h4 {
          margin: 0 0 5px 0;
          font-size: 1.1rem;
        }

        .timeline-content p {
          margin: 0;
          color: #cccccc;
          font-size: 0.95rem;
        }

        .warning-card,
        .security-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 20px;
          margin: 20px 0;
        }

        .warning-card {
          border-left: 4px solid #ffaa00;
        }

        .security-card {
          border-left: 4px solid #00aa66;
        }

        .contact-info {
          margin: 20px 0;
        }

        .contact-item {
          display: flex;
          align-items: center;
          margin: 15px 0;
          padding: 15px;
          background: rgba(255, 255, 255, 0.03);
          border-radius: 10px;
        }

        .contact-icon {
          font-size: 1.5rem;
          margin-right: 15px;
        }

        .response-time {
          background: rgba(254, 44, 85, 0.1);
          border: 1px solid #fe2c55;
          border-radius: 10px;
          padding: 15px;
          margin: 20px 0;
          text-align: center;
        }

        .footer-info {
          margin: 40px 0;
          padding: 20px 0;
          border-top: 1px solid #333333;
          text-align: center;
          color: #888888;
          font-size: 0.9rem;
        }

        .back-section {
          text-align: center;
          margin-top: 40px;
        }

        .back-button {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 12px 25px;
          background: linear-gradient(135deg, #fe2c55, #ff6b9d);
          color: white;
          text-decoration: none;
          border-radius: 12px;
          font-weight: 600;
          transition: all 0.3s ease;
          box-shadow: 0 5px 15px rgba(254, 44, 85, 0.3);
        }

        .back-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(254, 44, 85, 0.4);
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .data-deletion-wrapper {
            padding: 20px 10px;
          }

          .data-deletion-container {
            padding: 25px 20px;
          }

          .page-title {
            font-size: 2rem;
          }

          .content-section h2 {
            font-size: 1.6rem;
          }

          .data-categories {
            grid-template-columns: 1fr;
          }

          .method-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .contact-item {
            flex-direction: column;
            align-items: flex-start;
            text-align: left;
          }

          .contact-icon {
            margin-bottom: 5px;
          }
        }

        @media (max-width: 480px) {
          .page-title {
            font-size: 1.6rem;
          }

          .content-section h2 {
            font-size: 1.4rem;
          }

          .timeline-item {
            flex-direction: column;
          }

          .timeline-marker {
            margin-bottom: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default DataDeletion;
